import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Separator } from 'app/components/ui/separator';
import { Switch } from 'app/components/ui/switch';
import {
  Store,
  CheckCircle,
  AlertTriangle,
  Save,
  Shield,
  Clock,
  Phone,
  Search,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';
import { useGlobalSlice } from 'app/slice';

interface GeneralSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public for all keys
const createGeneralSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 1 for all general settings
  const settingsKeys = [
    'site_name',
    'store_name',
    'store_email',
    'store_phone',
    'store_country',
    'store_state',
    'store_city',
    'store_pincode',
    'store_address',
    'store_address2',
    'default_country_code',
    'email_verification_enabled',
    'mobile_verification_enabled',
    'download_expiry_days',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      let value = formData[key];

      // Convert boolean values to 0/1 for email_verification_enabled and mobile_verification_enabled
      if (
        key === 'email_verification_enabled' ||
        key === 'mobile_verification_enabled'
      ) {
        value = value ? 1 : 0;
      }

      payload[key] = {
        option_value: value,
        is_public: 1,
      };
    }
  });

  return payload;
};

const countries = [
  'India',
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Japan',
  'China',
  'Brazil',
  'Mexico',
  'South Africa',
];

// Helper function to safely convert any value to boolean for switch components
const getSwitchValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value === '1' || value.toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return Boolean(value);
};

export default function GeneralSettings({ form }: GeneralSettingsProps) {
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();
  const { useGetCountriesQuery } = useGlobalSlice();
  const [getCountries, { data: countriesData, isLoading: isLoadingCountries }] =
    useGetCountriesQuery();

  // Function to handle settings update with is_public for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createGeneralSettingsPayload(formData);

      console.log('General settings payload with is_public:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('General settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating general settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  // Filter countries based on search term
  const filteredCountries =
    countriesData?.data?.filter(
      country =>
        country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
        country.phonecode.includes(countrySearchTerm),
    ) || [];

  return (
    <>
      {/* Store Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="w-5 h-5" />
            Store Information
          </CardTitle>
          <CardDescription>
            Basic information about your store and business.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="site_name">Site Name *</Label>
              <Input
                id="site_name"
                {...form.register('site_name')}
                placeholder="Enter site name"
              />
              <p className="text-sm text-muted-foreground mt-2">
                The name that appears in browser tabs and search results. This
                is your website's public identity.
                <br />
                <span className="text-xs">
                  Example: "TechStore" or "My Fashion Boutique"
                </span>
              </p>
              {form.formState.errors.site_name && (
                <p className="text-sm text-red-500">
                  {String(form.formState.errors.site_name.message)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_name">Store Name *</Label>
              <Input
                id="store_name"
                {...form.register('store_name')}
                placeholder="Enter store name"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Your business name as it appears to customers. Used in receipts,
                emails, and throughout the store.
                <br />
                <span className="text-xs">
                  Example: "TechStore Inc." or "Fashion Boutique LLC"
                </span>
              </p>
              {form.formState.errors.store_name && (
                <p className="text-sm text-red-500">
                  {String(form.formState.errors.store_name.message)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_tagline">Store Tagline</Label>
              <Input
                id="store_tagline"
                {...form.register('store_tagline')}
                placeholder="Enter store tagline"
              />
              <p className="text-sm text-muted-foreground mt-2">
                A short, memorable phrase that describes your brand. Appears in
                headers and marketing materials.
                <br />
                <span className="text-xs">
                  Example: "Quality Tech for Everyone" or "Style Meets Comfort"
                </span>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_legal_business_name">
                Legal Business Name
              </Label>
              <Input
                id="store_legal_business_name"
                {...form.register('store_legal_business_name')}
                placeholder="Enter legal business name"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Your official registered business name for legal documents,
                invoices, and compliance purposes.
                <br />
                <span className="text-xs">
                  Example: "TechStore International Inc." or "Fashion Boutique
                  Enterprises LLC"
                </span>
              </p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="store_email">Store Email</Label>
              <Input
                id="store_email"
                type="email"
                {...form.register('store_email')}
                placeholder="support@example.com"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Primary contact email for customer support and business
                communications.
                <br />
                <span className="text-xs">
                  Example: "support@techstore.com" or
                  "hello@fashionboutique.com"
                </span>
              </p>
              {form.formState.errors.store_email && (
                <p className="text-sm text-red-500">
                  {String(form.formState.errors.store_email.message)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_phone">Store Phone</Label>
              <Input
                id="store_phone"
                {...form.register('store_phone')}
                placeholder="+1 (555) 123-4567"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Business phone number for customer inquiries and support calls.
                <br />
                <span className="text-xs">
                  Example: "+1 (555) 123-4567" or "+44 20 7946 0958"
                </span>
              </p>
            </div>
          </div>

          <Separator />

          {/* Default Country Code Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5" />
              <Label className="text-base font-medium">
                Default Country Code
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default_country_code">
                Default Country Code for Phone Numbers
              </Label>
              <Select
                value={form.watch('default_country_code')}
                onValueChange={value =>
                  form.setValue('default_country_code', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select default country code" />
                </SelectTrigger>
                <SelectContent>
                  {/* Search Input */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search countries..."
                        value={countrySearchTerm}
                        onChange={e => setCountrySearchTerm(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>

                  {/* Countries List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map(country => (
                        <SelectItem key={country.iso2} value={country.iso2}>
                          <div className="flex items-center gap-2">
                            <span>{country.emoji}</span>
                            <span>{country.name}</span>
                            <span className="text-muted-foreground">
                              (+{country.phonecode})
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No countries found
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Set the default country code that will be pre-selected for phone
                number inputs throughout your application.
                <br />
                <span className="text-xs">
                  This helps users by automatically selecting the most common
                  country code for your business.
                </span>
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label>Store Address</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
              <div className="space-y-2">
                <Label htmlFor="store_country">Country</Label>
                <Select
                  value={form.watch('store_country')}
                  onValueChange={value => form.setValue('store_country', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-2">
                  Your business's registered country for tax and legal
                  compliance.
                  <br />
                  <span className="text-xs">
                    Example: "United States" or "United Kingdom"
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="store_city">City</Label>
                <Input
                  id="store_city"
                  {...form.register('store_city')}
                  placeholder="Enter city"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  The city where your business is located or operates from.
                  <br />
                  <span className="text-xs">
                    Example: "New York" or "London"
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="store_state">State/Province</Label>
                <Input
                  id="store_state"
                  {...form.register('store_state')}
                  placeholder="Enter state"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  State or province for local tax and regulatory requirements.
                  <br />
                  <span className="text-xs">
                    Example: "California" or "England"
                  </span>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="store_pincode">Postal Code</Label>
                <Input
                  id="store_pincode"
                  {...form.register('store_pincode')}
                  placeholder="Enter postal code"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  ZIP code or postal code for shipping calculations and address
                  verification.
                  <br />
                  <span className="text-xs">
                    Example: "10001" or "SW1A 1AA"
                  </span>
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_address">Address Line 1</Label>
              <Input
                id="store_address"
                {...form.register('store_address')}
                placeholder="Enter address line 1"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Primary street address of your business location.
                <br />
                <span className="text-xs">
                  Example: "123 Main Street" or "456 Oxford Street"
                </span>
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_address2">Address Line 2</Label>
              <Input
                id="store_address2"
                {...form.register('store_address2')}
                placeholder="Enter address line 2 (optional)"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Additional address details like suite number, building name, or
                floor.
                <br />
                <span className="text-xs">
                  Example: "Suite 100" or "Floor 3, Building A"
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Verification & Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Verification & Security Settings
          </CardTitle>
          <CardDescription>
            Configure user verification requirements and security settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="email_verification_enabled">
                    Enable Email Verification
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, users must verify their email address during
                    registration.
                    <br />
                    <span className="text-xs">
                      Enable this for better security and account validation.
                    </span>
                  </p>
                </div>
                <Switch
                  id="email_verification_enabled"
                  checked={getSwitchValue(
                    form.watch('email_verification_enabled'),
                  )}
                  onCheckedChange={checked =>
                    form.setValue('email_verification_enabled', checked ? 1 : 0)
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="mobile_verification_enabled">
                    Enable Mobile Verification
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, users must verify their mobile number during
                    registration.
                    <br />
                    <span className="text-xs">
                      Enable this for better account security and communication.
                    </span>
                  </p>
                </div>
                <Switch
                  id="mobile_verification_enabled"
                  checked={getSwitchValue(
                    form.watch('mobile_verification_enabled'),
                  )}
                  onCheckedChange={checked =>
                    form.setValue(
                      'mobile_verification_enabled',
                      checked ? 1 : 0,
                    )
                  }
                />
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5" />
              <Label className="text-base font-medium">Download Settings</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="download_expiry_days">
                Download Expiry (Days)
              </Label>
              <Input
                id="download_expiry_days"
                type="number"
                min="1"
                max="365"
                {...form.register('download_expiry_days', {
                  valueAsNumber: true,
                  min: { value: 1, message: 'Minimum 1 day' },
                  max: { value: 365, message: 'Maximum 365 days' },
                })}
                placeholder="1"
              />
              <p className="text-sm text-muted-foreground">
                Number of days after which download links expire. This helps
                protect your content and manage bandwidth usage.
                <br />
                <span className="text-xs">
                  Example: 1 day (download links expire after 24 hours)
                </span>
              </p>
              {form.formState.errors.download_expiry_days && (
                <p className="text-sm text-red-500">
                  {String(form.formState.errors.download_expiry_days.message)}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  General settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save general settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
